import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from, switchMap, catchError, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { emulatorPort } from '../utils/emulator-port';
import { ErrorService } from './error.service';
import firebase from 'firebase/compat/app';

export interface ProfilePhotoUploadResponse {
    success: boolean;
    message: string;
    photoURL?: string;
    photoThumbnail?: string;
}

export interface ProfilePhotoRemoveResponse {
    success: boolean;
    message: string;
}

const suffix = '-default';

/**
 * Service for managing user profile photos
 * Handles upload, remove, and syncing to organizations
 */
@Injectable({
    providedIn: 'root',
})
export class ProfilePhotoService {
    constructor(
        private http: HttpClient,
        private errorService: ErrorService
    ) {}

    /**
     * Upload a profile photo
     * @param imageBlob The cropped image blob to upload
     * @returns Observable with upload result
     */
    public uploadProfilePhoto(imageBlob: Blob): Observable<ProfilePhotoUploadResponse> {
        return from(this.getIdToken()).pipe(
            switchMap((idToken) => {
                if (!idToken) {
                    throw new Error('User not authenticated');
                }

                const url = this.getApiUrl(`userUploadProfilePhoto${suffix}`);

                // Create FormData for file upload
                const formData = new FormData();
                formData.append('photo', imageBlob, 'profile-photo.jpg');

                const headers = new HttpHeaders({
                    Authorization: `Bearer ${idToken}`,
                    // Note: Don't set Content-Type for FormData - browser sets it with boundary
                });

                return this.http.post<ProfilePhotoUploadResponse>(url, formData, { headers });
            }),
            catchError((error) => this.handleError<ProfilePhotoUploadResponse>('uploadProfilePhoto', error))
        );
    }

    /**
     * Remove the user's profile photo
     * @returns Observable with remove result
     */
    public removeProfilePhoto(): Observable<ProfilePhotoRemoveResponse> {
        return from(this.getIdToken()).pipe(
            switchMap((idToken) => {
                if (!idToken) {
                    throw new Error('User not authenticated');
                }

                const url = this.getApiUrl(`userRemoveProfilePhoto${suffix}`);

                const headers = new HttpHeaders({
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${idToken}`,
                });

                return this.http.post<ProfilePhotoRemoveResponse>(url, {}, { headers });
            }),
            catchError((error) => this.handleError<ProfilePhotoRemoveResponse>('removeProfilePhoto', error))
        );
    }

    /**
     * Get the current user's ID token
     */
    private async getIdToken(): Promise<string | null> {
        const currentUser = firebase.auth().currentUser;
        if (!currentUser) {
            return null;
        }
        return currentUser.getIdToken();
    }

    /**
     * Build the API URL for a function
     */
    private getApiUrl(functionName: string): string {
        // Handle emulator vs production
        if (environment.useEmulators) {
            return `http://localhost:${emulatorPort(5001)}/${environment.firebase.projectId}/us-central1/api-${functionName}`;
        }
        return `https://us-central1-${environment.firebase.projectId}.cloudfunctions.net/api-${functionName}`;
    }

    /**
     * Handle API errors
     */
    private handleError<T>(operation: string, error: any): Observable<T> {
        const errorMsg = `Error in ${operation}: ${error.message || error}`;
        console.error(errorMsg, error);

        // Return a failure response
        return of({
            success: false,
            message: errorMsg,
        } as unknown as T);
    }
}
